import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { ApplicantInterviewerFeedback } from '@app/models/recruiting/applicant-interviewer-feedbacks.model';
import { ApplicantInterviewers } from '@app/models/recruiting/applicant-interviewers.model';
import { ApplicantInterviews } from '@app/models/recruiting/applicant-interviews.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { InterviewQuestion } from '@app/models/recruiting/interview-question.model';
import { ApplicantInterviewQuestionTypes } from '@app/modules/applicant-tracker/enums/applicant-interview-question-types.enum';
import { InterviewEvaluation } from '@app/modules/applicant-tracker/enums/interviewer-evaluation.enum';
import { JobPostingInterviewOpinionScaleTypes } from '@app/modules/applicant-tracker/enums/job-posting-interview-opinion-scale-types.enum';
import { AnalyticService, NotifyService } from '@app/services';

@Component({
    templateUrl: './interviews-store.template.html',
    styleUrls: ['./interviews-store.style.scss'],
})
export class InterviewsStoreComponent {
    applicantInterview: ApplicantInterviews;
    interviewQuestions: InterviewQuestion[] = [];
    interviewFeedbacks: ApplicantInterviewerFeedback[] = [];
    applicantInterviewer: ApplicantInterviewers;
    applicantInterviewQuestionTypes = ApplicantInterviewQuestionTypes;
    jobPostingInterviewOpinionScaleTypes = JobPostingInterviewOpinionScaleTypes;

    evaluation: InterviewEvaluation;
    interviewerEvaluationsTypes = InterviewEvaluation;
    isSecondaryVariant = false;
    isLoading = false;
    allowSubmission = false;
    isSaving = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private analyticService: AnalyticService
    ) {
        this.applicantInterview = this.route.snapshot.data.models.get('applicantInterview');

        // Will only have one interviewer since we are filling out the questions from single interviewers perspective.
        this.applicantInterviewer = this.applicantInterview.applicantInterviewer;

        this.interviewQuestions = this.applicantInterview.applicantInterviewer.applicantInterviewerFeedbacks.map(
            (applicantInterviewerFeedbacks: ApplicantInterviewerFeedback) =>
                applicantInterviewerFeedbacks.interviewQuestion
        );

        this.interviewFeedbacks = this.applicantInterviewer.applicantInterviewerFeedbacks;
    }

    goToApplicantPage(applicant: Applicant): void {
        this.router.navigate(['/recruiting', 'applicants', applicant.id]);
    }

    async onOpnionScaleChange(scaleValue: string[], interviewFeedback: ApplicantInterviewerFeedback): Promise<void> {
        interviewFeedback.feedback = scaleValue;

        this.isSaving = true;

        try {
            await interviewFeedback.save();
        } catch (err) {
            this.notify.error(err);
        }

        this.isSaving = false;
    }

    getTextForOpenQuestion(interviewFeedback: ApplicantInterviewerFeedback): string {
        if (!interviewFeedback) {
            return;
        }

        if (!interviewFeedback?.feedback) {
            interviewFeedback.feedback = [''];
            return interviewFeedback.feedback[0];
        }

        return interviewFeedback.feedback[0];
    }

    async onTextForOpenChange(text: string, interviewFeedback: ApplicantInterviewerFeedback): Promise<void> {
        interviewFeedback.feedback = [text];

        this.isSaving = true;

        try {
            await interviewFeedback.save();
        } catch (err) {
            this.notify.error(err);
        }

        this.isSaving = false;
    }

    getOpinionScaleValue(interviewFeedback: ApplicantInterviewerFeedback): number {
        if (!interviewFeedback || !interviewFeedback?.feedback) {
            return;
        }

        return Number(interviewFeedback.feedback[0]);
    }

    getMultipleChoiceValue(interviewFeedback: ApplicantInterviewerFeedback): string[] {
        if (!interviewFeedback) {
            return;
        }

        if (!interviewFeedback.feedback) {
            return [];
        }

        return interviewFeedback.feedback;
    }

    async onMultipleChoiceChange(values: string[], interviewFeedback: ApplicantInterviewerFeedback): Promise<void> {
        if (!values.length) {
            return;
        }

        interviewFeedback.feedback = values;

        this.isSaving = true;

        try {
            await interviewFeedback.save();
        } catch (err) {
            this.notify.error(err);
        }

        this.isSaving = false;
    }

    getYesNoValue(interviewFeedback: ApplicantInterviewerFeedback): string {
        if (!interviewFeedback) {
            return;
        }

        if (!interviewFeedback?.feedback) {
            return;
        }

        return interviewFeedback.feedback[0];
    }

    async onYesNoValueChange(value: string, interviewFeedback: ApplicantInterviewerFeedback): Promise<void> {
        interviewFeedback.feedback = [value];

        this.isSaving = true;

        try {
            await interviewFeedback.save();
        } catch (err) {
            this.notify.error(err);
        }

        this.isSaving = false;
    }

    async submitEvaluation(): Promise<void> {
        this.checkIfFormValid();

        if (this.allowSubmission) {
            this.isLoading = true;

            try {
                this.applicantInterviewer.isSubmitted = true;
                await this.applicantInterviewer.save();
                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitingJobPostingInterviewsApplicantInterviewInterviewFeedbackSubmit
                );
                this.router.navigate([
                    'recruiting',
                    'jobPostings',
                    this.applicantInterview.interview.atsJobId,
                    'interviews',
                    'applicantInterviews',
                    this.applicantInterview.id,
                ]);
            } catch (err) {
                this.notify.error(err);
            }

            this.isLoading = false;
        }
    }

    private checkIfFormValid(): void {
        this.allowSubmission = true;

        this.interviewQuestions.forEach((interviewQuestion: InterviewQuestion) => {
            const currentInterviewFeedback = this.interviewFeedbacks.find(
                (interviewFeedback: ApplicantInterviewerFeedback) => {
                    return interviewQuestion.id === interviewFeedback.atsInterviewQuestionId;
                }
            );

            // Check if the question is required and if there is a value for the feedback property
            if (
                interviewQuestion.isRequired &&
                (!currentInterviewFeedback.feedback || currentInterviewFeedback.feedback.length === 0)
            ) {
                this.allowSubmission = false;
            }
        });
    }
}
